import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import { ConfirmationModal } from '../common';
import {
  SignInSettings,
  domainExpertApi,
  expertKeys,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';
import { Switch, btnPrimary } from './ui';

interface SignInOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  enabled: boolean;
  readOnly: boolean;
}

export default function SignInOptionsModal({ isOpen, onClose, enabled, readOnly }: SignInOptionsModalProps) {
  const queryClient = useQueryClient();
  const [confirmPasswordOff, setConfirmPasswordOff] = useState(false);

  const { data, isInitialLoading, isError } = useQuery({
    queryKey: expertKeys.signIn(),
    queryFn: domainExpertApi.getSignInSettings,
    enabled: enabled && isOpen,
  });

  const mutation = useMutation({
    mutationFn: domainExpertApi.updateSignInSettings,
    onSuccess: (updated) => {
      queryClient.setQueryData<SignInSettings>(expertKeys.signIn(), updated);
      toast.success('Sign-in options updated');
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      toast.error(getExpertErrorMessage(err, "We couldn't update sign-in options. Please try again."));
    },
  });

  const googleOn = !!data?.google_sso_enabled;
  const togglesDisabled = !googleOn || readOnly || mutation.isLoading;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Sign-in options"
        size="sm"
        footer={
          <button type="button" onClick={onClose} className={btnPrimary}>
            Done
          </button>
        }
      >
        {isInitialLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading…</p>
        ) : isError || !data ? (
          <p className="py-6 text-center text-sm text-red-600">We couldn't load sign-in options. Please try again.</p>
        ) : (
          <div className="space-y-6">
            <div className="border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">Google sign-in</p>
                <span
                  className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                    googleOn ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {googleOn ? 'On' : 'Off'}
                </span>
              </div>
              {googleOn && data.allowed_google_domains.length > 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  Allowed company domains: {data.allowed_google_domains.join(', ')}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">Managed by LAWMA.</p>
            </div>

            <Switch
              id="signin-auto-join"
              label="Let anyone with an allowed company Google account join automatically"
              checked={data.auto_join_enabled}
              disabled={togglesDisabled}
              onChange={(checked) => mutation.mutate({ auto_join_enabled: checked })}
            />

            <Switch
              id="signin-password"
              label="Allow password sign-in"
              description="Users can sign in with an email and password."
              checked={data.password_login_enabled}
              disabled={togglesDisabled}
              onChange={(checked) => {
                if (!checked) setConfirmPasswordOff(true);
                else mutation.mutate({ password_login_enabled: true });
              }}
            />

            {!googleOn && (
              <p className="text-xs text-gray-500">These options are available when Google sign-in is on.</p>
            )}
          </div>
        )}
      </Modal>

      <ConfirmationModal
        isOpen={confirmPasswordOff}
        onClose={() => setConfirmPasswordOff(false)}
        onConfirm={() => {
          setConfirmPasswordOff(false);
          mutation.mutate({ password_login_enabled: false });
        }}
        title="Turn off password sign-in?"
        message="Users will only be able to sign in with Google. Anyone without an allowed company Google account will lose access."
        confirmText="Turn off"
        type="warning"
      />
    </>
  );
}
